// AdminAttestationsPage — Liste admin des demandes d'attestation
// Utilise Refine useTable + shadcn Table/Tabs/Badge

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTable } from '@refinedev/core'
import { Loader2, FileText } from 'lucide-react'
// Layout is handled by route-level AdminLayout
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'

// Types pour les attestations admin
interface AttestationItem {
  id: string
  entreprise: {
    raisonSociale: string
  }
  prenomGerant: string
  nomGerant: string
  certificationType: string
  casques: number
  statut: 'en_attente' | 'validee' | 'refusee' | 'generee'
  createdAt: string
}

type StatutFilter = 'toutes' | 'en_attente' | 'validee' | 'refusee'

function getStatusBadge(statut: AttestationItem['statut']) {
  switch (statut) {
    case 'en_attente':
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          En attente
        </Badge>
      )
    case 'validee':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          Validée
        </Badge>
      )
    case 'refusee':
      return <Badge variant="destructive">Refusée</Badge>
    case 'generee':
      return <Badge variant="secondary">Générée</Badge>
    default:
      return <Badge variant="outline">{statut}</Badge>
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function CasquesDisplay({ count }: { count: number }) {
  const maxCasques = 5
  const filled = Math.min(count, maxCasques)
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: maxCasques }, (_, i) => (
        <div
          key={i}
          className={`size-3 rounded-full ${
            i < filled ? 'bg-primary' : 'bg-muted'
          }`}
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">{count}/5</span>
    </div>
  )
}

export default function AdminAttestationsPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<StatutFilter>('toutes')

  const { tableQuery, currentPage, setCurrentPage, pageCount } =
    useTable<AttestationItem>({
      resource: 'attestations',
      pagination: {
        currentPage: 1,
        pageSize: 20,
      },
      filters: {
        permanent:
          activeTab !== 'toutes'
            ? [{ field: 'statut', operator: 'eq', value: activeTab }]
            : [],
      },
    })

  const attestations = tableQuery.data?.data ?? []
  const total = tableQuery.data?.total ?? 0
  const isLoading = tableQuery.isLoading

  const handleTabChange = (value: string) => {
    setActiveTab(value as StatutFilter)
    setCurrentPage(1)
  }

  const handleRowClick = (id: string) => {
    navigate(`/admin/attestations/${id}`)
  }

  return (
    <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Attestations</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Gérez les demandes d'attestation des entreprises
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="size-4" />
            <span>{total} demande{total !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="toutes">Toutes</TabsTrigger>
            <TabsTrigger value="en_attente">En attente</TabsTrigger>
            <TabsTrigger value="validee">Validées</TabsTrigger>
            <TabsTrigger value="refusee">Refusées</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {isLoading ? (
              <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
              </div>
            ) : attestations.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Aucune attestation dans cette catégorie.
              </p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Gérant</TableHead>
                      <TableHead>Type certif.</TableHead>
                      <TableHead>Casques</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attestations.map((attestation) => (
                      <TableRow
                        key={attestation.id}
                        className="cursor-pointer"
                        onClick={() => handleRowClick(String(attestation.id))}
                      >
                        <TableCell className="font-medium">
                          {attestation.entreprise?.raisonSociale || '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {attestation.prenomGerant} {attestation.nomGerant}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {attestation.certificationType || '—'}
                        </TableCell>
                        <TableCell>
                          <CasquesDisplay count={attestation.casques} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(attestation.createdAt)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(attestation.statut)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {pageCount > 1 && (
                  <div className="flex items-center justify-between border-t pt-4 mt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {currentPage} sur {pageCount} ({total} résultats)
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage- 1)}
                        disabled={currentPage<= 1}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage+ 1)}
                        disabled={currentPage>= pageCount}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
    </div>
  )
}
